<template>
    <div class="cup-sub-tabs">
        <div class="sub-tabs-switcher">
            <button
                v-for="tab in subTabs"
                :key="tab.key"
                @click="activeSubTab = tab.key"
                class="sub-switch-btn"
                :class="{
                    active: activeSubTab === tab.key,
                    'sub-switch-btn--medal': !!tab.medal,
                }"
                :aria-label="tab.ariaLabel || undefined"
            >
                <img
                    v-if="tab.medal"
                    :src="`/images/icons/medal-${tab.medal}.svg`"
                    alt=""
                    class="sub-switch-btn__medal"
                    aria-hidden="true"
                />
                <span v-else class="sub-switch-btn__label">{{ tab.label }}</span>
                <span v-if="stagePeriods[tab.key]" class="sub-switch-btn__period">{{ stagePeriods[tab.key] }}</span>
            </button>
        </div>

        <div class="sub-tab-content">
            <!-- Групповой этап -->
            <cup-group-stage v-if="activeSubTab === 'group_stage'" :cup-groups="cupGroups" :group-games="groupGames" />

            <!-- Дополнительный этап -->
            <cup-playoff-table v-if="activeSubTab === 'dop'" :teams="playoffTeams" :games="playoffGames" />

            <!-- Полуфиналы -->
            <cup-playoff-matches v-if="activeSubTab === 'semifinal'" :playoff-games="getStageGames('semifinal')" />

            <!-- Матч за 3 место -->
            <cup-playoff-matches v-if="activeSubTab === 'third_place'" :playoff-games="getStageGames('third_place')" />

            <!-- Матч за Кубок -->
            <cup-playoff-matches v-if="activeSubTab === 'final'" :playoff-games="getStageGames('final')" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import CupGroupStage from '@/components/features/results/CupGroupStage.vue';
import CupPlayoffTable from '@/components/features/results/CupPlayoffTable.vue';
import CupPlayoffMatches from '@/components/features/results/CupPlayoffMatches.vue';
import { formatGamesPeriod } from '@/utils/gameDateRange';

const props = defineProps({
    groupGames: {
        type: Array,
        default: () => []
    },
    cupGroups: {
        type: Array,
        default: () => []
    },
    playoffGames: {
        type: Array,
        default: () => []
    },
    playoffTeams: {
        type: Array,
        default: () => []
    },
    cupPlayoffGames: {
        type: Array,
        default: () => []
    }
});

const activeSubTab = ref('group_stage');

const subTabs = [
    { key: 'group_stage', label: 'Групповой этап' },
    { key: 'dop', label: 'Дополнительный этап' },
    { key: 'semifinal', label: 'Полуфиналы' },
    { key: 'third_place', label: 'Матч за 3 место', medal: 'bronze', ariaLabel: 'Матч за 3 место' },
    { key: 'final', label: 'Матч за Кубок', medal: 'gold', ariaLabel: 'Матч за Кубок' },
];

const getStageGames = (stage) => {
    if (Array.isArray(props.cupPlayoffGames)) {
        const stageGroup = props.cupPlayoffGames.find(g => g.stage === stage);
        return stageGroup ? [stageGroup] : [];
    }
    return props.cupPlayoffGames[stage] ? [props.cupPlayoffGames[stage]] : [];
};

const getStageMatchGames = (stage) => {
    const groups = getStageGames(stage);
    return groups[0]?.games || [];
};

const stagePeriods = computed(() => ({
    group_stage: formatGamesPeriod(props.groupGames),
    dop: formatGamesPeriod(props.playoffGames),
    semifinal: formatGamesPeriod(getStageMatchGames('semifinal')),
    third_place: formatGamesPeriod(getStageMatchGames('third_place')),
    final: formatGamesPeriod(getStageMatchGames('final')),
}));
</script>

<style lang="scss" scoped>
.sub-tab-content {
    min-height: 400px;
}

.cup-sub-tabs {
    .sub-tabs-switcher {
        display: inline-flex;
        gap: 0.25rem;
        background: #f1f5f9;
        padding: 0.25rem;
        border-radius: 6px;
        margin-bottom: 1.25rem;
        flex-wrap: wrap;

        .sub-switch-btn {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            gap: 0.15rem;
            padding: 0.5rem 1rem;
            background: transparent;
            border: none;
            border-radius: 4px;
            font-size: 0.875rem;
            font-weight: 500;
            color: #275092;
            cursor: pointer;
            transition: all 0.15s ease;
            white-space: nowrap;
            text-align: center;

            &:hover {
                color: #475569;
            }

            &.active {
                background: #fff;
                color: #275092;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            }

            &--medal {
                padding: 0.35rem 0.5rem;
            }
        }

        .sub-switch-btn__medal {
            width: 22px;
            height: 22px;
            object-fit: contain;
            display: block;
            flex-shrink: 0;
        }

        .sub-switch-btn__period {
            font-size: 0.65rem;
            font-weight: 500;
            line-height: 1.2;
            color: #64748b;
            white-space: nowrap;
        }
    }

    .sub-tab-content {
        min-height: 300px;

        .view-switcher {
            display: inline-flex;
            gap: 0.25rem;
            background: #f1f5f9;
            padding: 0.25rem;
            border-radius: 6px;
            margin-bottom: 1.5rem;

            .view-btn {
                padding: 0.5rem 1rem;
                background: transparent;
                border: none;
                border-radius: 4px;
                font-size: 0.875rem;
                font-weight: 500;
                color: #275092;
                cursor: pointer;
                transition: all 0.15s ease;

                &:hover {
                    color: #475569;
                }

                &.active {
                    background: #fff;
                    color: #275092;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
                }
            }
        }
    }
}
</style>
